import {
  CmpInvoiceDisplayStateEnum,
  CmpInvoicePaymentRuleKindEnum,
} from "../../../graphql/types";
import { cmpLongDateFormatter } from "../../common/dashboard/formatters";

// Shared invoice formatting + presentation helpers.
//
// These live here so the manage-invoices table (ManageInvoices.tsx) and the
// invoice document / modals (ManageInvoiceActionModals.tsx) render money, dates,
// document titles, watermarks, and the preview action bar from a single source
// of truth instead of duplicating the logic in both files.

export const currencyFormatter = new Intl.NumberFormat("en-CA", {
  currency: "CAD",
  style: "currency",
});

export const dateFormatter = cmpLongDateFormatter;

export const formatCurrency = (amountInCents?: number | null) =>
  currencyFormatter.format((amountInCents || 0) / 100);

export const formatDate = (utcMilli?: number | null) =>
  utcMilli ? dateFormatter.format(new Date(utcMilli)) : "-";

const getOrdinalDaySuffix = (day: number) => {
  if (day % 100 >= 11 && day % 100 <= 13) {
    return "th";
  }

  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

// Ordinal "July 1st, 2026" form used by the invoice document's coverage-period
// sentences; mirrors the backend PDF's formatOrdinalLongDate verbatim so the
// on-screen preview and the emailed PDF read identically (spec §4).
export const formatOrdinalLongDate = (utcMilli?: number | null) => {
  if (!utcMilli) {
    return "Not set";
  }

  const date = new Date(utcMilli);

  return `${date.toLocaleDateString("en-US", { month: "long" })} ${date.getDate()}${getOrdinalDaySuffix(date.getDate())}, ${date.getFullYear()}`;
};

export const getRecordedPaymentMethodLabel = (label?: string | null) => {
  const trimmedLabel = (label || "").trim();
  const normalizedLabel = trimmedLabel.toLowerCase();

  if (
    !trimmedLabel ||
    trimmedLabel === "-" ||
    normalizedLabel === "not paid" ||
    normalizedLabel === "paid"
  ) {
    return "";
  }

  return trimmedLabel;
};

// Title printed at the top of the invoice document / receipt.
export const getInvoiceDocumentTitle = ({
  displayState,
  hasPayments,
}: {
  displayState?: CmpInvoiceDisplayStateEnum | null;
  hasPayments?: boolean;
}) => {
  if (displayState === CmpInvoiceDisplayStateEnum.Draft) {
    return "DRAFT INVOICE";
  }

  // A reversed invoice keeps the PAID INVOICE title; the REVERSED watermark
  // communicates the reversal.
  if (displayState === CmpInvoiceDisplayStateEnum.Refunded) {
    return "PAID INVOICE";
  }

  // Any invoice with a recorded payment renders as a receipt — including a
  // partially-paid installment plan that is still Outstanding.
  if (hasPayments && displayState !== CmpInvoiceDisplayStateEnum.Voided) {
    return "PAYMENT RECEIPT";
  }

  return "INVOICE";
};

export const getInvoiceWatermark = (
  displayState?: CmpInvoiceDisplayStateEnum | null
) => {
  if (displayState === CmpInvoiceDisplayStateEnum.Voided) {
    return "CANCELLED";
  }

  if (displayState === CmpInvoiceDisplayStateEnum.Refunded) {
    return "REVERSED";
  }

  return null;
};

export const isGroupInsuranceRule = (
  ruleKind?: CmpInvoicePaymentRuleKindEnum | null
) => ruleKind === CmpInvoicePaymentRuleKindEnum.InsuranceDirectDepositOnly;

export type InvoicePreviewActionKey =
  | "printPdf"
  | "addPayment"
  | "approveSave"
  | "approveSend"
  | "sendInvoice"
  | "sendReceipt";

export type InvoicePreviewAction = {
  key: InvoicePreviewActionKey;
  label: string;
  color: "gold" | "green";
  withEmailPreview?: boolean;
  caption?: string;
  previewSubline?: string;
};

const PRINT_PDF_ACTION: InvoicePreviewAction = {
  key: "printPdf",
  label: "Print PDF",
  color: "gold",
};

const SEND_INVOICE_CAPTION =
  "Invoice has been generated and will be emailed to contact below.";
const PAID_RECEIPT_CAPTION =
  "Invoice has been paid and will be emailed to contact below.";
const PAYMENT_RECEIPT_CAPTION =
  "Payment has been received and will be emailed to contact below.";

// Single source of truth for which buttons the invoice preview shows, per state.
// Keeps the document title (getInvoiceDocumentTitle) and the action bar in sync —
// both are driven off the same `hasPayments` signal.
export const getInvoicePreviewActions = ({
  displayState,
  hasPayments,
  ruleKind,
  canApplyPayment,
  canSendInvoice,
  sendIsResend,
}: {
  displayState?: CmpInvoiceDisplayStateEnum | null;
  hasPayments: boolean;
  ruleKind?: CmpInvoicePaymentRuleKindEnum | null;
  canApplyPayment: boolean;
  canSendInvoice: boolean;
  sendIsResend: boolean;
}): InvoicePreviewAction[] => {
  const groupInsurance = isGroupInsuranceRule(ruleKind);

  const receiptAction = (label: string, caption: string): InvoicePreviewAction => ({
    key: "sendReceipt",
    label,
    color: "gold",
    withEmailPreview: true,
    caption,
    previewSubline: "To be sent with invoice receipt",
  });

  if (displayState === CmpInvoiceDisplayStateEnum.Draft) {
    // INV-PREVIEW-002: the read-only invoice preview must not offer
    // "Approve and Save" — approve-without-send belongs exclusively to the Edit
    // Draft flow / Create page header. The `approveSave` key + its onApproveSave
    // handler plumbing are intentionally kept elsewhere for that flow.
    return [
      PRINT_PDF_ACTION,
      {
        key: "approveSend",
        label: "Approve and Send",
        color: "green",
        withEmailPreview: true,
        caption: SEND_INVOICE_CAPTION,
        previewSubline: "To be sent with invoice",
      },
    ];
  }

  // Group insurance is settled out-of-band via chamber direct deposit, so the
  // only online action is printing — in every state.
  if (groupInsurance) {
    return [PRINT_PDF_ACTION];
  }

  if (displayState === CmpInvoiceDisplayStateEnum.Paid && hasPayments) {
    return [PRINT_PDF_ACTION, receiptAction("Resend Invoice Receipt", PAID_RECEIPT_CAPTION)];
  }

  if (displayState === CmpInvoiceDisplayStateEnum.Refunded) {
    return [PRINT_PDF_ACTION, receiptAction("Send Invoice Receipt", PAID_RECEIPT_CAPTION)];
  }

  if (displayState === CmpInvoiceDisplayStateEnum.Voided) {
    return [PRINT_PDF_ACTION];
  }

  // Outstanding / not paid (covers partially-paid installment plans).
  const actions: InvoicePreviewAction[] = [];

  if (canApplyPayment) {
    actions.push({ key: "addPayment", label: "Add Payment", color: "green" });
  }

  actions.push(PRINT_PDF_ACTION);

  if (hasPayments) {
    actions.push(receiptAction("Send Payment Receipt", PAYMENT_RECEIPT_CAPTION));
  } else if (canSendInvoice) {
    actions.push({
      key: "sendInvoice",
      label: sendIsResend ? "Resend Invoice" : "Send Invoice",
      color: "gold",
      withEmailPreview: true,
      caption: SEND_INVOICE_CAPTION,
      previewSubline: "To be sent with invoice",
    });
  }

  return actions;
};
